import React from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { DashboardEditorProps } from "components/ComponentType";
import { T } from "services/lang/LanguageService";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { Column, ColumnEditorOptions } from "primereact/column";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { SankeyDiagramLink, SankeyDiagramProps } from "../sankey-types";

const LinksEditor = ({ config, onConfigChange }: DashboardEditorProps<SankeyDiagramProps>) => {
  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const linksToUpdate = [...config.graph.links!];
    const { newData } = e;
    const updatedLinks = linksToUpdate.map(link => {
      if (link.identifier === newData.identifier) {
        return { ...link, ...newData };
      }
      return link;
    });

    const updatedConfig = {
      ...config,
      graph: { ...config.graph, links: updatedLinks },
    };
    onConfigChange(updatedConfig);
  };

  const sourceBodyTemplate = (rowData: SankeyDiagramLink) => {
    return config.graph.nodes.find(node => node.id === rowData.source)?.title;
  };

  const targetBodyTemplate = (rowData: SankeyDiagramLink) => {
    return config.graph.nodes.find(node => node.id === rowData.target)?.title;
  };

  const renderTitle = (rowData: SankeyDiagramLink) => {
    return <DatasetTitle identifier={rowData.identifier} />;
  };

  const unitEditor = (options: ColumnEditorOptions) => {
    return (
      <DatasetUnitSelector
        className="w-full"
        value={options.value as string}
        onChange={e => options.editorCallback && options.editorCallback(e.value as string)}
      />
    );
  };

  const numberEditor = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        inputStyle={{ width: "100%", height: "100%" }}
        value={options.value as number}
        onChange={e => options.editorCallback && options.editorCallback(e.value as number)}
      />
    );
  };

  const dropdownEditor = (options: ColumnEditorOptions) => {
    const nodes =
      config.graph?.nodes?.map(node => {
        return { label: node.title, value: node.id };
      }) || [];

    return (
      <Dropdown
        value={options.value}
        options={nodes}
        optionLabel="label"
        onChange={e => options.editorCallback && options.editorCallback(e.value as string)}
        style={{ width: "100%" }}
      />
    );
  };

  return (
    <DataTable
      value={config.graph.links}
      onRowEditComplete={onRowEditComplete}
      editMode="row"
      dataKey="identifier"
    >
      <Column field="identifier" header={T("word.identifier")} body={renderTitle} />
      <Column
        field="source"
        header={T("word.source")}
        editor={dropdownEditor}
        body={sourceBodyTemplate}
      />
      <Column
        field="target"
        header={T("word.target")}
        editor={dropdownEditor}
        body={targetBodyTemplate}
      />
      <Column field="customUnit" header={T("word.unit")} editor={unitEditor} />
      <Column field="customDivider" header={T("word.divider")} editor={numberEditor} />
      <Column rowEditor align="center" style={{ width: "10%" }} />
    </DataTable>
  );
};

export default LinksEditor;
